import { useEffect, useMemo, useState } from 'react';
import {
  ShieldCheckIcon,
  ScaleIcon,
  AcademicCapIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import policyService from '../services/policy.service';
import type { PolicyResponse, RuleCategoryKey } from '../types/policy.types';
import RuleCard from '../components/policy/RuleCard';
import AuthenticatedLayout from '../components/AuthenticatedLayout';

type Filter = 'all' | RuleCategoryKey;

const CATEGORY_ICON: Record<RuleCategoryKey, React.ElementType> = {
  compliance: ShieldCheckIcon,
  legal_risk: ScaleIcon,
  qa: AcademicCapIcon,
};

function formatDate(iso: string, locale: string) {
  try {
    return new Date(iso).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

export default function EvaluationCriteria() {
  const { t, i18n } = useTranslation('policy');
  const [policy, setPolicy] = useState<PolicyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    policyService
      .getMyPolicy()
      .then((data) => {
        if (!cancelled) setPolicy(data);
      })
      .catch(() => {
        if (!cancelled) setError(t('errors.load', { defaultValue: 'Failed to load your policy.' }));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  const filteredCategories = useMemo(() => {
    if (!policy) return [];
    const q = search.trim().toLowerCase();
    return policy.categories
      .filter((c) => filter === 'all' || c.category === filter)
      .map((c) => ({
        ...c,
        rules: c.rules.filter((r) => {
          if (!q) return true;
          const hay = [
            r.name,
            r.description_short,
            r.description_long,
            ...r.regulation_refs.map((x) => `${x.jurisdiction} ${x.law}`),
          ]
            .join(' ')
            .toLowerCase();
          return hay.includes(q);
        }),
      }))
      .filter((c) => c.rules.length > 0 || filter !== 'all');
  }, [policy, filter, search]);

  return (
    <AuthenticatedLayout>
    <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {t('page.title', { defaultValue: 'My Policy' })}
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-gray-500">
          {t('page.subtitle', {
            defaultValue:
              'These are the rules CallCov evaluates on every call you process. Your team configures them with us — this page is the source of truth.',
          })}
        </p>
      </header>

      {loading && (
        <div className="flex justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-indigo-600" />
        </div>
      )}

      {error && !loading && (
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">{error}</div>
      )}

      {!loading && !error && policy && (
        <>
          {/* Hero stats */}
          <section className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200">
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                {t('summary.rulesActive', { defaultValue: 'Rules active' })}
              </dt>
              <dd className="mt-1 text-2xl font-bold text-gray-900">{policy.summary.rules_active}</dd>
            </div>
            <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200">
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                {t('summary.regulations', { defaultValue: 'Regulations covered' })}
              </dt>
              <dd className="mt-1 text-2xl font-bold text-gray-900">
                {policy.summary.regulations_covered}
              </dd>
            </div>
            <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200">
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                {t('summary.lastUpdated', { defaultValue: 'Last updated' })}
              </dt>
              <dd className="mt-1 text-2xl font-bold text-gray-900">
                {formatDate(policy.policy_last_updated_at, i18n.language)}
              </dd>
              <dd className="mt-0.5 text-xs text-gray-500">
                {policy.is_default_config
                  ? t('summary.defaultConfig', { defaultValue: 'Default configuration' })
                  : policy.config_name}
              </dd>
            </div>
          </section>

          {/* Filters */}
          <section className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <FilterChip active={filter === 'all'} onClick={() => setFilter('all')} label={t('filters.all', { defaultValue: 'All' })} />
              {(['compliance', 'legal_risk', 'qa'] as RuleCategoryKey[]).map((c) => {
                const Icon = CATEGORY_ICON[c];
                return (
                  <FilterChip
                    key={c}
                    active={filter === c}
                    onClick={() => setFilter(c)}
                    label={
                      <span className="inline-flex items-center gap-1">
                        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                        {t(`categories.${c}.label`, {
                          defaultValue:
                            c === 'legal_risk'
                              ? 'Legal Risk'
                              : c === 'compliance'
                              ? 'Compliance'
                              : 'QA',
                        })}
                      </span>
                    }
                  />
                );
              })}
            </div>
            <div className="relative w-full sm:max-w-xs">
              <MagnifyingGlassIcon
                className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('filters.searchPlaceholder', { defaultValue: 'Search rules or regulations' })}
                className="w-full rounded-md border-gray-300 pl-8 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
          </section>

          {/* Categories */}
          <div className="space-y-8">
            {filteredCategories.map((cat) => {
              const Icon = CATEGORY_ICON[cat.category];
              return (
                <section key={cat.category} id={`policy-${cat.category}`}>
                  <header className="mb-3 flex items-center gap-2 border-b border-gray-200 pb-2">
                    <Icon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    <h2 className="text-lg font-semibold text-gray-900">{cat.label}</h2>
                    <span className="text-sm text-gray-500">({cat.rules.length})</span>
                  </header>
                  {cat.rules.length === 0 ? (
                    <p className="rounded-md bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
                      {t('emptyCategory', {
                        defaultValue: 'No rules in this category match your filters.',
                      })}
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                      {cat.rules.map((r) => (
                        <RuleCard key={r.slug} rule={r} />
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        </>
      )}
    </main>
    </AuthenticatedLayout>
  );
}

interface FilterChipProps {
  active: boolean;
  onClick: () => void;
  label: React.ReactNode;
}

function FilterChip({ active, onClick, label }: FilterChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset transition ${
        active
          ? 'bg-indigo-600 text-white ring-indigo-600'
          : 'bg-white text-gray-700 ring-gray-300 hover:bg-gray-50'
      }`}
    >
      {label}
    </button>
  );
}
